import {
  CHARGE_PER_STEP_KWH,
  CHARGE_STOP_SOC_RATIO,
  CHARGE_TRIGGER_SOC_RATIO,
  CHARGER_POWER_KW,
} from "./constants";
import { formatStepTime } from "./time";
import type {
  ChargingEvent,
  ChargingStationAgent,
  SimulationState,
  StationId,
  TripPlan,
  VehicleAgent,
} from "./types";

const cloneStations = (stations: SimulationState["stations"]) => {
  const next = {} as SimulationState["stations"];
  (Object.keys(stations) as StationId[]).forEach((stationId) => {
    next[stationId] = {
      ...stations[stationId],
      chargers: stations[stationId].chargers.map((charger) => ({ ...charger })),
      waitingQueue: [...stations[stationId].waitingQueue],
      chargingVehicleIds: [...stations[stationId].chargingVehicleIds],
      demandProfileKw: [...stations[stationId].demandProfileKw],
    };
  });
  return next;
};

const socRatio = (vehicle: VehicleAgent) => vehicle.socKWh / vehicle.batteryCapacityKWh;

const removeVehicleFromStationState = (
  stations: Record<StationId, ChargingStationAgent>,
  vehicleId: string,
) => {
  (Object.keys(stations) as StationId[]).forEach((stationId) => {
    const station = stations[stationId];
    station.waitingQueue = station.waitingQueue.filter((id) => id !== vehicleId);
    station.chargingVehicleIds = station.chargingVehicleIds.filter((id) => id !== vehicleId);
    station.chargers = station.chargers.map((charger) =>
      charger.vehicleId === vehicleId ? { ...charger, vehicleId: null } : charger,
    );
  });
};

const pushEvent = (
  events: ChargingEvent[],
  step: number,
  event: string,
  maxEvents = 200,
) => {
  events.push({ step, timeLabel: formatStepTime(step), event });
  if (events.length > maxEvents) {
    events.shift();
  }
};

const findCurrentTrip = (vehicle: VehicleAgent): TripPlan | null =>
  vehicle.trips.find((trip) => trip.id === vehicle.activeTripId) || null;

const applyTripDepartures = (
  vehicles: VehicleAgent[],
  stations: Record<StationId, ChargingStationAgent>,
  step: number,
  events: ChargingEvent[],
) => {
  vehicles.forEach((vehicle) => {
    const departingTrip = vehicle.trips.find(
      (trip) => !trip.completed && trip.departureStep === step,
    );
    if (!departingTrip) {
      return;
    }

    removeVehicleFromStationState(stations, vehicle.id);
    vehicle.currentState = "driving";
    vehicle.activeTripId = departingTrip.id;
    vehicle.position = {
      zone: "road",
      stationId: vehicle.homeStationId,
      roadProgress: 0,
    };
    pushEvent(
      events,
      step,
      `${vehicle.id} started trip (${departingTrip.distanceKm} km from ${vehicle.homeStationId}).`,
    );
  });
};

const applyTripReturns = (
  vehicles: VehicleAgent[],
  stations: Record<StationId, ChargingStationAgent>,
  step: number,
  events: ChargingEvent[],
) => {
  vehicles.forEach((vehicle) => {
    if (vehicle.currentState !== "driving" || !vehicle.activeTripId) {
      return;
    }

    const trip = findCurrentTrip(vehicle);
    if (!trip || trip.returnStep !== step) {
      if (trip) {
        const progress = (step - trip.departureStep) / (trip.returnStep - trip.departureStep || 1);
        vehicle.position = {
          zone: "road",
          stationId: vehicle.homeStationId,
          roadProgress: Math.max(0, Math.min(1, progress)),
        };
      }
      return;
    }

    trip.completed = true;
    vehicle.activeTripId = null;
    vehicle.socKWh = Math.max(0, Number((vehicle.socKWh - trip.energyConsumptionKWh).toFixed(2)));
    vehicle.currentState = "idle";
    vehicle.position = { zone: "station", stationId: vehicle.homeStationId };
    pushEvent(events, step, `${vehicle.id} returned with ${(socRatio(vehicle) * 100).toFixed(1)}% SOC.`);

    if (socRatio(vehicle) < CHARGE_TRIGGER_SOC_RATIO) {
      const station = stations[vehicle.homeStationId];
      if (!station.waitingQueue.includes(vehicle.id)) {
        station.waitingQueue.push(vehicle.id);
      }
      vehicle.currentState = "waiting_for_charge";
      vehicle.position = { zone: "queue", stationId: vehicle.homeStationId };
      pushEvent(events, step, `${vehicle.id} queued at ${vehicle.homeStationId} for charging.`);
    }
  });
};

const assignChargers = (
  vehicles: VehicleAgent[],
  stations: Record<StationId, ChargingStationAgent>,
  step: number,
  events: ChargingEvent[],
) => {
  const vehicleMap = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]));

  (Object.keys(stations) as StationId[]).forEach((stationId) => {
    const station = stations[stationId];
    station.chargers.forEach((charger, chargerIndex) => {
      if (charger.vehicleId) {
        return;
      }
      const nextVehicleId = station.waitingQueue.shift();
      if (!nextVehicleId) {
        return;
      }

      const vehicle = vehicleMap.get(nextVehicleId);
      if (!vehicle) {
        return;
      }

      charger.vehicleId = vehicle.id;
      if (!station.chargingVehicleIds.includes(vehicle.id)) {
        station.chargingVehicleIds.push(vehicle.id);
      }

      vehicle.currentState = "charging";
      vehicle.position = {
        zone: "charger",
        stationId,
        chargerIndex,
      };
      pushEvent(events, step, `${vehicle.id} started charging at ${stationId} charger ${chargerIndex + 1}.`);
    });
  });
};

const applyCharging = (
  vehicles: VehicleAgent[],
  stations: Record<StationId, ChargingStationAgent>,
  step: number,
  events: ChargingEvent[],
) => {
  const vehicleMap = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]));

  (Object.keys(stations) as StationId[]).forEach((stationId) => {
    const station = stations[stationId];
    station.chargers.forEach((charger, chargerIndex) => {
      if (!charger.vehicleId) {
        return;
      }

      const vehicle = vehicleMap.get(charger.vehicleId);
      if (!vehicle) {
        const staleVehicleId = charger.vehicleId;
        charger.vehicleId = null;
        station.chargingVehicleIds = station.chargingVehicleIds.filter((id) => id !== staleVehicleId);
        return;
      }

      const targetKWh = vehicle.batteryCapacityKWh * CHARGE_STOP_SOC_RATIO;
      const remainingKWh = Math.max(0, targetKWh - vehicle.socKWh);
      const chargedKWh = Math.min(CHARGE_PER_STEP_KWH, remainingKWh);
      vehicle.socKWh = Number((vehicle.socKWh + chargedKWh).toFixed(2));

      if (vehicle.socKWh >= targetKWh - 0.0001) {
        vehicle.currentState = "idle";
        vehicle.position = { zone: "station", stationId: vehicle.homeStationId };
        station.chargingVehicleIds = station.chargingVehicleIds.filter((id) => id !== vehicle.id);
        charger.vehicleId = null;
        pushEvent(
          events,
          step,
          `${vehicle.id} reached ${(CHARGE_STOP_SOC_RATIO * 100).toFixed(0)}% SOC and left ${stationId} charger ${chargerIndex + 1}.`,
        );
      }
    });

    const activeChargers = station.chargers.filter((charger) => Boolean(charger.vehicleId)).length;
    station.demandProfileKw.push(activeChargers * CHARGER_POWER_KW);
  });
};

export const runSimulationStep = (state: SimulationState): SimulationState => {
  if (state.completed || !state.running) {
    return state;
  }

  const step = state.step;
  const vehicles = state.vehicles.map((vehicle) => ({
    ...vehicle,
    trips: vehicle.trips.map((trip) => ({ ...trip })),
    position: { ...vehicle.position },
  }));
  const stations = cloneStations(state.stations);
  const chargingEventLog = [...state.chargingEventLog];

  applyTripDepartures(vehicles, stations, step, chargingEventLog);
  applyTripReturns(vehicles, stations, step, chargingEventLog);
  assignChargers(vehicles, stations, step, chargingEventLog);
  applyCharging(vehicles, stations, step, chargingEventLog);

  const nextStep = step + 1;
  const completed = nextStep >= state.totalSteps;

  return {
    ...state,
    vehicles,
    stations,
    chargingEventLog,
    step: completed ? state.totalSteps : nextStep,
    completed,
    running: completed ? false : state.running,
  };
};
